import { useState } from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send, Instagram } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { COMPANY, waLink, WA_MESSAGES } from "@/lib/site";

const CARDS = [
  { icon: Phone, title: "Telefone / WhatsApp", value: COMPANY.phoneDisplay, href: () => waLink(WA_MESSAGES.default) },
  { icon: Mail, title: "E-mail", value: COMPANY.email, href: () => `mailto:${COMPANY.email}` },
  { icon: MapPin, title: "Endereço", value: `${COMPANY.address.street}, ${COMPANY.address.city} - ${COMPANY.address.state}` },
  { icon: Clock, title: "Atendimento", value: COMPANY.hours },
];

export default function Contato() {
  const [form, setForm] = useState({ nome: "", contato: "", assunto: "", mensagem: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const msg = `Olá, vim pelo site da Miríade Turismo.%0A%0A*Nome:* ${form.nome}%0A*Contato:* ${form.contato}%0A*Assunto:* ${form.assunto}%0A*Mensagem:* ${form.mensagem}`;
    window.open(`https://wa.me/${COMPANY.whatsappNumber}?text=${msg}`, "_blank");
  };

  const mapsQuery = encodeURIComponent(
    `${COMPANY.address.street}, ${COMPANY.address.city} - ${COMPANY.address.state}, ${COMPANY.address.zip}`
  );

  return (
    <>
      <PageHero
        overline="Estamos por perto"
        title="Fale com a Miríade Turismo"
        subtitle="Estamos prontos para ajudar você a planejar sua próxima viagem com mais tranquilidade."
        seoTitle="Contato | Miríade Turismo"
        seoDescription="Fale com a Miríade Turismo. Telefone, WhatsApp, e-mail e endereço da agência de viagens em São José do Rio Preto."
      />

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CARDS.map((c, i) => {
              const Inner = (
                <div data-testid={`contact-card-${i}`} className="h-full rounded-3xl border border-border bg-white p-7 shadow-soft transition-all hover:-translate-y-1 hover:shadow-hover">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light">
                    <c.icon size={24} className="text-brand" />
                  </div>
                  <h3 className="mt-5 font-heading text-sm font-semibold uppercase tracking-wide text-brand">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{c.value}</p>
                </div>
              );
              return (
                <Reveal key={c.title} delay={i * 0.06}>
                  {c.href ? (
                    <a href={c.href()} target="_blank" rel="noopener noreferrer">{Inner}</a>
                  ) : Inner}
                </Reveal>
              );
            })}
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {/* Form */}
            <Reveal>
              <div className="rounded-3xl border border-border bg-white p-7 shadow-premium sm:p-9">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-ink">Envie uma mensagem</h2>
                <p className="mt-2 text-sm text-ink-soft">Preencha os dados e enviaremos sua mensagem direto para o nosso WhatsApp.</p>
                <form onSubmit={submit} data-testid="contact-form" className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Nome</label>
                    <input data-testid="contact-name" required value={form.nome} onChange={set("nome")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-brand" placeholder="Seu nome" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Telefone ou e-mail</label>
                    <input data-testid="contact-contact" required value={form.contato} onChange={set("contato")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-brand" placeholder="(00) 00000-0000" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Assunto</label>
                    <input data-testid="contact-subject" required value={form.assunto} onChange={set("assunto")} className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-brand" placeholder="Sobre o que deseja falar?" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink">Mensagem</label>
                    <textarea data-testid="contact-message" required rows={4} value={form.mensagem} onChange={set("mensagem")} className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-brand" placeholder="Conte um pouco sobre a viagem que você imagina..." />
                  </div>
                  <button type="submit" data-testid="contact-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:scale-[1.01] hover:bg-[#1ebe5d]">
                    <Send size={18} /> Enviar pelo WhatsApp
                  </button>
                </form>
              </div>
            </Reveal>

            {/* Map + quick contact */}
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col gap-6">
                <div className="overflow-hidden rounded-3xl border border-border shadow-soft">
                  <iframe
                    title="Localização Miríade Turismo"
                    data-testid="contact-map"
                    src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
                    className="h-72 w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="rounded-3xl border border-brand-light bg-brand-light/60 p-7">
                  <h3 className="font-heading text-lg font-semibold text-ink">Prefere falar agora?</h3>
                  <p className="mt-2 text-sm text-ink-soft">Chame a nossa equipe no WhatsApp e receba atendimento rápido.</p>
                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <a href={waLink(WA_MESSAGES.default)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-[1.02]">
                      <MessageCircle size={18} /> WhatsApp
                    </a>
                    <a href={COMPANY.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-brand/30 bg-white px-6 py-3 text-sm font-semibold text-brand transition-all hover:bg-brand-light">
                      <Instagram size={18} /> Instagram
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
